import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { BarChart } from '@mui/x-charts/BarChart'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { DownloadIcon, InfoIcon } from 'src/v3/core/icons'
import {
  fetchLearnerSnapshot,
  selectLearnerSnapshot,
  selectLearnerSnapshotStatus,
} from 'src/v3/content-pipeline/performance.slice'
import type { QuizScoreBucket } from 'src/v3/content-pipeline/performance.slice'
import {
  deriveKPIs,
  deriveTrendSeries,
  deriveCategoryTrendSeries,
  deriveCategoryStats,
  deriveLicenseBreakdown,
  resolveLicenses,
  smoothSeries,
  toShareSeries,
  DerivedKPIs,
  LicenseBreakdownRow,
  TrendPoint,
  CategoryTrendPoint,
  CategoryStat,
  LicenseKey,
  LICENSE_KEYS,
  LICENSE_LABELS,
  LICENSE_COLORS,
} from '../utils/learner-snapshot-utils'
import { LicenseCategory } from './performance-filters'

type LearningAnalyticsSectionProps = {
  domain: 'live' | 'enduring'
  itemID: string
  timeRangeMonths: number
  licenseFilter: LicenseCategory
}

type WindowKey = '1' | '3' | '6' | '12' | '24' | '36' | 'all'

type WindowOption = {
  key: WindowKey
  label: string
  months: number | null
}

let WINDOW_OPTIONS: WindowOption[] = [
  { key: '1', label: '1M', months: 1 },
  { key: '3', label: '3M', months: 3 },
  { key: '6', label: '6M', months: 6 },
  { key: '12', label: '1Y', months: 12 },
  { key: '24', label: '2Y', months: 24 },
  { key: '36', label: '3Y', months: 36 },
  { key: 'all', label: 'All', months: null },
]

type ViewMode = 'overlay' | 'stack' | 'share'
type Metric = 'completions' | 'enrollments'

let globalFilterToInitialSet = (filter: LicenseCategory): Set<LicenseKey> => {
  if (filter === 'All') return new Set(LICENSE_KEYS)
  let key = filter.toLowerCase() as LicenseKey
  return new Set([key])
}

let KPICard = ({
  label,
  value,
  unit,
  delta,
  deltaLabel,
  sparkline,
  sparkColor,
  context,
  tooltip,
}: {
  label: string
  value: string
  unit?: string
  delta: number | null
  deltaLabel?: string
  sparkline?: number[]
  sparkColor?: string
  context: string
  tooltip?: string
}) => {
  let deltaColor =
    delta === null
      ? 'text.secondary'
      : delta > 0
        ? 'success.main'
        : delta < 0
          ? 'error.main'
          : 'text.secondary'
  let deltaPrefix = delta === null ? '' : delta > 0 ? '+' : ''
  let deltaSuffix = deltaLabel || '% vs prev period'

  let labelEl = (
    <Typography color="text.secondary" variant="overline">
      {label}
    </Typography>
  )

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: 'background.paperDark',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {tooltip ? <Tooltip title={tooltip}>{labelEl}</Tooltip> : labelEl}
      <Typography color="text.secondary" variant="caption">
        {context}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          mt: 1,
          gap: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h4">{value}</Typography>
            {unit && (
              <Typography color="text.secondary" variant="h6">
                {unit}
              </Typography>
            )}
          </Box>
          {delta !== null ? (
            <Typography
              variant="caption"
              sx={{
                color: deltaColor,
              }}
            >
              {deltaPrefix}
              {delta}
              {deltaSuffix}
            </Typography>
          ) : (
            <Typography color="text.secondary" variant="caption">
              — Not enough data
            </Typography>
          )}
        </Box>
        {sparkline && sparkline.length > 1 && (
          <Box sx={{ width: 80, height: 32, flexShrink: 0 }}>
            <SparkLineChart
              margin={{ top: 2, bottom: 2, left: 0, right: 0 }}
              height={32}
              data={sparkline}
              area
              sx={{
                color: sparkColor || '#1976d2',
              }}
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}

let LicenseToggleRow = ({
  selected,
  onChange,
}: {
  selected: Set<LicenseKey>
  onChange: (next: Set<LicenseKey>) => void
}) => {
  let toggle = (key: LicenseKey) => {
    let next = new Set(selected)
    if (next.has(key)) {
      if (next.size > 1) next.delete(key)
    } else {
      next.add(key)
    }
    onChange(next)
  }
  let isolate = (key: LicenseKey) => onChange(new Set([key]))
  let selectAll = () => onChange(new Set(LICENSE_KEYS))

  let allSelected = selected.size === LICENSE_KEYS.length

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
    >
      <Typography color="text.secondary" variant="body2">
        Licenses:
      </Typography>
      {LICENSE_KEYS.map((key) => {
        let isSelected = selected.has(key)
        return (
          <Chip
            key={key}
            label={LICENSE_LABELS[key]}
            size="small"
            onClick={() => toggle(key)}
            onDoubleClick={() => isolate(key)}
            variant={isSelected ? 'filled' : 'outlined'}
            sx={{
              bgcolor: isSelected ? LICENSE_COLORS[key] : 'transparent',
              color: isSelected ? '#fff' : 'text.secondary',
              borderColor: LICENSE_COLORS[key],
              '&:hover': {
                bgcolor: isSelected ? LICENSE_COLORS[key] : 'action.hover',
              },
            }}
          />
        )
      })}
      <Chip
        label={allSelected ? 'All selected' : 'Select all'}
        size="small"
        variant="outlined"
        onClick={selectAll}
      />
    </Box>
  )
}

let WindowChipRow = ({
  value,
  onChange,
}: {
  value: WindowKey
  onChange: (next: WindowKey) => void
}) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={value}
    onChange={(_, next) => next && onChange(next as WindowKey)}
    aria-label="Time window"
  >
    {WINDOW_OPTIONS.map((opt) => (
      <ToggleButton key={opt.key} value={opt.key}>
        {opt.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
)

let TrendChart = ({
  data,
  smoothingWeeks,
  licensesLabel,
}: {
  data: TrendPoint[]
  smoothingWeeks: number
  licensesLabel: string
}) => {
  if (data.length === 0) return null

  let smoothed = smoothSeries(
    data,
    ['enrolled', 'completed'] as (keyof TrendPoint)[],
    smoothingWeeks,
  )

  let dateRange = `${data[0].date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${data[data.length - 1].date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`

  return (
    <Card
      variant="outlined"
      sx={{ p: 3, bgcolor: 'background.paperDark' }}
      className="min-w-0"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Enrollment & Completion Trend</Typography>
          <Typography color="text.secondary" variant="caption">
            Weekly · {dateRange} · {licensesLabel}
            {smoothingWeeks > 1 ? ` · ${smoothingWeeks}-week avg` : ''}
          </Typography>
        </Box>
      </Box>
      <LineChart
        height={320}
        dataset={smoothed.map((d) => ({
          date: d.date,
          enrolled: d.enrolled,
          completed: d.completed,
        }))}
        xAxis={[
          {
            dataKey: 'date',
            scaleType: 'time',
            valueFormatter: (v: Date, ctx) =>
              ctx.location === 'tick'
                ? v.toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                  })
                : v.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }),
          },
        ]}
        yAxis={[{ min: 0 }]}
        series={[
          {
            dataKey: 'enrolled',
            label: 'Enrolled',
            color: '#1976d2',
            area: true,
            showMark: false,
            curve: 'monotoneX',
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'completed',
            label: 'Completed',
            color: '#66bb6a',
            area: true,
            showMark: false,
            curve: 'monotoneX',
            valueFormatter: nullSafeValueFormatter,
          },
        ]}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { position: { vertical: 'top', horizontal: 'end' } },
        }}
        axisHighlight={{ x: 'line', y: 'none' }}
        sx={{
          '& .MuiAreaElement-root': { opacity: 0.18 },
          '& .MuiChartsGrid-line': { stroke: 'rgba(255,255,255,0.06)' },
        }}
      />
    </Card>
  )
}

let LicenseBreakdownChart = ({
  data,
  timeLabel,
  selected,
  onToggle,
}: {
  data: LicenseBreakdownRow[]
  timeLabel: string
  selected: Set<LicenseKey>
  onToggle: (key: LicenseKey) => void
}) => {
  if (data.length === 0) return null

  return (
    <Card
      variant="outlined"
      sx={{ p: 3, bgcolor: 'background.paperDark' }}
      className="min-w-0"
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Completions by License</Typography>
        <Typography color="text.secondary" variant="caption">
          {timeLabel} · click a bar to toggle
        </Typography>
      </Box>
      <BarChart
        height={260}
        layout="horizontal"
        dataset={data.map((d) => ({
          label: d.label,
          count: d.count,
          key: d.key,
        }))}
        yAxis={[
          {
            scaleType: 'band',
            dataKey: 'label',
            colorMap: {
              type: 'ordinal',
              values: data.map((d) => d.label),
              colors: data.map((d) =>
                selected.has(d.key) ? LICENSE_COLORS[d.key] : '#555',
              ),
            },
          },
        ]}
        xAxis={[{ min: 0 }]}
        series={[
          {
            dataKey: 'count',
            label: 'Completions',
            barLabel: 'value',
            valueFormatter: nullSafeValueFormatter,
          },
        ]}
        grid={{ vertical: true }}
        onItemClick={(_, item) => {
          let row = data[item.dataIndex]
          if (row) onToggle(row.key)
        }}
        slotProps={{ legend: { hidden: true } as any }}
        sx={{
          '& .MuiChartsGrid-line': { stroke: 'rgba(255,255,255,0.06)' },
          '& .MuiBarElement-root': { cursor: 'pointer' },
        }}
      />
    </Card>
  )
}

let CategoryTrendChart = ({
  data,
  timeLabel,
  selected,
  viewMode,
  metric,
  smoothingWeeks,
  onViewChange,
  onMetricChange,
}: {
  data: CategoryTrendPoint[]
  timeLabel: string
  selected: Set<LicenseKey>
  viewMode: ViewMode
  metric: Metric
  smoothingWeeks: number
  onViewChange: (v: ViewMode) => void
  onMetricChange: (m: Metric) => void
}) => {
  if (data.length === 0) return null

  let smoothed = smoothSeries(
    data,
    LICENSE_KEYS as (keyof CategoryTrendPoint)[],
    smoothingWeeks,
  )
  let displayData = viewMode === 'share' ? toShareSeries(smoothed) : smoothed

  let visibleLicenses = LICENSE_KEYS.filter((k) => selected.has(k))
  let stack = viewMode === 'stack' || viewMode === 'share' ? 'total' : undefined

  return (
    <Card
      variant="outlined"
      sx={{ p: 3, bgcolor: 'background.paperDark' }}
      className="min-w-0"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">
            {metric === 'completions' ? 'Completions' : 'Enrollments'} Over Time
            by License
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Weekly · {timeLabel}
            {smoothingWeeks > 1 ? ` · ${smoothingWeeks}-week avg` : ''}
            {viewMode === 'share' ? ' · % of total' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, v) => v && onMetricChange(v)}
            aria-label="Metric"
          >
            <ToggleButton value="completions">Completions</ToggleButton>
            <ToggleButton value="enrollments">Enrollments</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, v) => v && onViewChange(v)}
            aria-label="View mode"
          >
            <Tooltip title="Overlay lines compare absolute volume across licenses">
              <ToggleButton value="overlay">Overlay</ToggleButton>
            </Tooltip>
            <Tooltip title="Stack to see contribution to total volume">
              <ToggleButton value="stack">Stack</ToggleButton>
            </Tooltip>
            <Tooltip title="Normalize to 100% to compare mix over time">
              <ToggleButton value="share">Share %</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <LineChart
        height={340}
        dataset={displayData as unknown as Record<string, unknown>[]}
        xAxis={[
          {
            dataKey: 'date',
            scaleType: 'time',
            valueFormatter: (v: Date, ctx) =>
              ctx.location === 'tick'
                ? v.toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                  })
                : v.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }),
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: viewMode === 'share' ? 100 : undefined,
            valueFormatter: (v: number) =>
              viewMode === 'share' ? `${v}%` : v.toLocaleString(),
          },
        ]}
        series={visibleLicenses.map((key) => ({
          dataKey: key,
          label: LICENSE_LABELS[key],
          color: LICENSE_COLORS[key],
          showMark: false,
          curve: 'monotoneX' as const,
          stack,
          area: viewMode !== 'overlay',
          valueFormatter: (v: number | null) =>
            v === null
              ? ''
              : viewMode === 'share'
                ? `${v}%`
                : v.toLocaleString(),
        }))}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { position: { vertical: 'top', horizontal: 'end' } },
        }}
        axisHighlight={{ x: 'line', y: 'none' }}
        sx={{
          '& .MuiAreaElement-root': { opacity: 0.25 },
          '& .MuiChartsGrid-line': { stroke: 'rgba(255,255,255,0.06)' },
        }}
      />
    </Card>
  )
}

let CategoryStatsTable = ({
  data,
  timeLabel,
  selected,
  onToggle,
}: {
  data: CategoryStat[]
  timeLabel: string
  selected: Set<LicenseKey>
  onToggle: (key: LicenseKey) => void
}) => {
  if (data.length === 0) return null

  return (
    <Card
      variant="outlined"
      sx={{ p: 3, bgcolor: 'background.paperDark', overflow: 'auto' }}
      className="min-w-0"
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Category Breakdown</Typography>
        <Typography color="text.secondary" variant="caption">
          {timeLabel} · period-over-period change · click row to toggle
        </Typography>
      </Box>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            px: 1.5,
            py: 1,
            textAlign: 'left',
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <thead>
          <tr>
            {[
              'Category',
              'Enrolled',
              'Completed',
              'Rate',
              'Share',
              'Enrolled Δ',
              'Rate Δ',
            ].map((heading) => (
              <th key={heading}>
                <Typography color="text.secondary" variant="overline">
                  {heading}
                </Typography>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            let isActive = selected.has(row.key)
            return (
              <Box
                component="tr"
                key={row.key}
                onClick={() => onToggle(row.key)}
                sx={{
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.45,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <td>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: LICENSE_COLORS[row.key],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2">{row.label}</Typography>
                  </Box>
                </td>
                <td>{row.enrolled.toLocaleString()}</td>
                <td>{row.completed.toLocaleString()}</td>
                <td>{row.completionRatePct}%</td>
                <td>{row.sharePct}%</td>
                <Box
                  component="td"
                  sx={{
                    color:
                      row.enrolledDelta === null
                        ? 'text.secondary'
                        : row.enrolledDelta > 0
                          ? 'success.main'
                          : row.enrolledDelta < 0
                            ? 'error.main'
                            : 'text.secondary',
                  }}
                >
                  {row.enrolledDelta === null
                    ? '—'
                    : `${row.enrolledDelta > 0 ? '+' : ''}${row.enrolledDelta}%`}
                </Box>
                <Box
                  component="td"
                  sx={{
                    color:
                      row.completionRateDelta === null
                        ? 'text.secondary'
                        : row.completionRateDelta > 0
                          ? 'success.main'
                          : row.completionRateDelta < 0
                            ? 'error.main'
                            : 'text.secondary',
                  }}
                >
                  {row.completionRateDelta === null
                    ? '—'
                    : `${row.completionRateDelta > 0 ? '+' : ''}${row.completionRateDelta} pts`}
                </Box>
              </Box>
            )
          })}
        </tbody>
      </Box>
    </Card>
  )
}

let QuizDistributionChart = ({ data }: { data: QuizScoreBucket[] }) => {
  if (data.length === 0) return null

  return (
    <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.paperDark' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Quiz Score Distribution</Typography>
        <Typography color="text.secondary" variant="caption">
          All attempts · all time
        </Typography>
      </Box>
      <BarChart
        height={260}
        dataset={data.map((d) => ({ range: d.range, count: d.count }))}
        xAxis={[{ scaleType: 'band', dataKey: 'range' }]}
        yAxis={[{ min: 0 }]}
        series={[
          {
            dataKey: 'count',
            label: 'Students',
            color: '#1976d2',
            valueFormatter: nullSafeValueFormatter,
          },
        ]}
        grid={{ horizontal: true }}
        axisHighlight={{ x: 'band', y: 'none' }}
        slotProps={{ legend: { hidden: true } as any }}
        sx={{
          borderRadius: 4,
          '& .MuiChartsGrid-line': { stroke: 'rgba(255,255,255,0.06)' },
        }}
      />
    </Card>
  )
}

let LoadingSkeleton = () => (
  <Box className="@container">
    <Box className="grid grid-cols-2 @5xl:grid-cols-4 gap-4 mb-6">
      {[...Array(4)].map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          sx={{
            height: 150,
          }}
        />
      ))}
    </Box>
    <Box className="grid grid-cols-1 @5xl:grid-cols-[3fr_2fr] gap-6">
      <Skeleton
        variant="rounded"
        sx={{
          height: 380,
        }}
      />
      <Skeleton
        variant="rounded"
        sx={{
          height: 380,
        }}
      />
    </Box>
  </Box>
)

let formatLicensesLabel = (selected: Set<LicenseKey>): string => {
  if (selected.size === 0) return 'no licenses'
  if (selected.size === LICENSE_KEYS.length) return 'all licenses'
  return LICENSE_KEYS.filter((k) => selected.has(k))
    .map((k) => LICENSE_LABELS[k])
    .join(', ')
}

let pickSmoothing = (weeks: number): number => {
  if (weeks <= 8) return 1
  if (weeks <= 26) return 2
  if (weeks <= 78) return 4
  return 8
}

export let LearningAnalyticsSection = ({
  domain,
  itemID,
  timeRangeMonths,
  licenseFilter,
}: LearningAnalyticsSectionProps) => {
  let dispatch = useAppDispatch()
  let snapshot = useAppSelector(selectLearnerSnapshot({ itemID, domain }))
  let status = useAppSelector(selectLearnerSnapshotStatus({ itemID, domain }))

  let [windowKey, setWindowKey] = useState<WindowKey>(() => {
    let match = WINDOW_OPTIONS.find((o) => o.months === timeRangeMonths)
    return match ? match.key : '12'
  })
  let [selectedLicenses, setSelectedLicenses] = useState<Set<LicenseKey>>(() =>
    globalFilterToInitialSet(licenseFilter),
  )
  let [viewMode, setViewMode] = useState<ViewMode>('overlay')
  let [metric, setMetric] = useState<Metric>('completions')
  let [smoothingOn, setSmoothingOn] = useState<boolean>(false)

  useEffect(() => {
    if (!status || status === 'uninitialized') {
      dispatch(fetchLearnerSnapshot({ itemID, domain }))
    }
  }, [dispatch, itemID, domain, status])

  useEffect(() => {
    setSelectedLicenses(globalFilterToInitialSet(licenseFilter))
  }, [licenseFilter])

  useEffect(() => {
    let match = WINDOW_OPTIONS.find((o) => o.months === timeRangeMonths)
    if (match) setWindowKey(match.key)
  }, [timeRangeMonths])

  let activeWindow = useMemo(
    () => WINDOW_OPTIONS.find((o) => o.key === windowKey) || WINDOW_OPTIONS[3],
    [windowKey],
  )
  let { months } = activeWindow
  let licenses = useMemo(
    () => resolveLicenses(selectedLicenses),
    [selectedLicenses],
  )

  let kpis: DerivedKPIs | null = useMemo(
    () => (snapshot ? deriveKPIs(snapshot, months, licenses) : null),
    [snapshot, months, licenses],
  )

  let trendData: TrendPoint[] = useMemo(
    () =>
      snapshot ? deriveTrendSeries(snapshot.series, months, licenses) : [],
    [snapshot, months, licenses],
  )

  let licenseBreakdown: LicenseBreakdownRow[] = useMemo(
    () => (snapshot ? deriveLicenseBreakdown(snapshot.series, months) : []),
    [snapshot, months],
  )

  let categoryTrend: CategoryTrendPoint[] = useMemo(
    () =>
      snapshot
        ? deriveCategoryTrendSeries(snapshot.series, months, metric)
        : [],
    [snapshot, months, metric],
  )

  let categoryStats: CategoryStat[] = useMemo(
    () => (snapshot ? deriveCategoryStats(snapshot.series, months) : []),
    [snapshot, months],
  )

  let smoothingWeeks = smoothingOn ? pickSmoothing(trendData.length) : 1
  let licensesLabel = formatLicensesLabel(selectedLicenses)

  let toggleLicense = (key: LicenseKey) => {
    let next = new Set(selectedLicenses)
    if (next.has(key)) {
      if (next.size > 1) next.delete(key)
    } else {
      next.add(key)
    }
    setSelectedLicenses(next)
  }

  let headerJSX = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h5" component="h2">
            Learning Analytics
          </Typography>
          <Chip label="Interactive" size="small" variant="outlined" />
        </Box>
        <Typography color="text.secondary" variant="body2">
          Explore enrollment, completion, and quiz performance. Controls below
          drive all charts in this section.
        </Typography>
      </Box>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        disabled
        size="small"
      >
        Export
      </Button>
    </Box>
  )

  if (status === 'pending' || !snapshot) {
    return (
      <Box>
        {headerJSX}
        <LoadingSkeleton />
      </Box>
    )
  }

  if (status === 'rejected') {
    return (
      <Box>
        {headerJSX}
        <Card
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paperDark' }}
        >
          <Typography color="error.main" variant="body1">
            Failed to load learning analytics.
          </Typography>
        </Card>
      </Box>
    )
  }

  let weeksCovered = kpis?.weeksCovered ?? 0
  let kpiContext = `${activeWindow.label} · ${licensesLabel}`

  let controlsCard = (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        mb: 3,
        bgcolor: 'background.paperDark',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography color="text.secondary" variant="body2">
            Window:
          </Typography>
          <WindowChipRow value={windowKey} onChange={setWindowKey} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButton
            size="small"
            value="smooth"
            selected={smoothingOn}
            onChange={() => setSmoothingOn((v) => !v)}
          >
            Smoothing
          </ToggleButton>
          {smoothingOn && smoothingWeeks > 1 && (
            <Typography color="text.secondary" variant="caption">
              {smoothingWeeks}-week avg
            </Typography>
          )}
          <Tooltip
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption">
                  Weekly data is spiky — one good week can hide the underlying
                  trend.
                </Typography>
                <Typography variant="caption">
                  Smoothing plots a rolling average instead of the raw weekly
                  count, flattening the jagged line into the actual direction
                  things are heading.
                </Typography>
                <Typography variant="caption">
                  The window auto-scales with your time range: shorter ranges
                  use a smaller average (or none) so you don&apos;t lose real
                  signal; longer ranges use a wider one so years of weekly
                  points stay readable.
                </Typography>
                <Typography variant="caption">
                  Turn it off to see exactly what happened in each individual
                  week.
                </Typography>
              </Box>
            }
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                color: 'text.secondary',
                cursor: 'help',
              }}
            >
              <InfoIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      <LicenseToggleRow
        selected={selectedLicenses}
        onChange={setSelectedLicenses}
      />
      <Typography color="text.secondary" variant="caption">
        Tip: click a license chip to toggle it. Double-click to isolate. Click a
        bar in the License chart or a row in the table to do the same.
      </Typography>
    </Card>
  )

  return (
    <Box className="@container">
      {headerJSX}
      {controlsCard}
      {kpis && (
        <Box className="grid grid-cols-2 @5xl:grid-cols-4 gap-4 mb-6">
          <KPICard
            label="TOTAL ENROLLED"
            context={kpiContext}
            value={kpis.totalEnrolled.toLocaleString()}
            delta={kpis.enrolledDelta}
            sparkline={kpis.enrolledSparkline}
            sparkColor="#1976d2"
            tooltip={`Students enrolled in the last ${activeWindow.label} across ${licensesLabel}. Sparkline shows weekly enrolled totals; delta compares the second half of the window to the first.`}
          />
          <KPICard
            label="COMPLETION RATE"
            context={kpiContext}
            value={String(kpis.completionRatePct)}
            unit="%"
            delta={kpis.completionRateDelta}
            deltaLabel=" pts vs prev period"
            sparkline={kpis.completionRateSparkline}
            sparkColor="#66bb6a"
            tooltip="Completions divided by enrollments for the selected window and licenses. Delta is in percentage points."
          />
          <KPICard
            label="1ST ATTEMPT PASS RATE"
            context={`${activeWindow.label} · all licenses`}
            value={String(kpis.firstAttemptPassRatePct)}
            unit="%"
            delta={kpis.firstAttemptDelta}
            deltaLabel=" pts vs prev period"
            sparkline={kpis.firstAttemptSparkline}
            sparkColor="#ffa726"
            tooltip="First-attempt passes divided by total quiz attempts. Quiz data is not broken out by license so this ignores the license filter."
          />
          <KPICard
            label="ACTIVE (30D)"
            context="Rolling · all licenses"
            value={kpis.activeLast30Days.toLocaleString()}
            delta={kpis.activeDelta}
            tooltip="Distinct learners active in the last 30 days. Provided directly by the API and does not respond to window or license filters."
          />
        </Box>
      )}
      <Box sx={{ mb: 1 }}>
        <Typography color="text.secondary" variant="caption">
          Showing {weeksCovered} weeks of data · {licensesLabel}
        </Typography>
      </Box>
      <Box className="grid grid-cols-1 @5xl:grid-cols-[3fr_2fr] gap-6 mb-6">
        <TrendChart
          data={trendData}
          smoothingWeeks={smoothingWeeks}
          licensesLabel={licensesLabel}
        />
        <LicenseBreakdownChart
          data={licenseBreakdown}
          timeLabel={activeWindow.label}
          selected={selectedLicenses}
          onToggle={toggleLicense}
        />
      </Box>
      <Box className="grid grid-cols-1 gap-6 mb-6">
        <CategoryTrendChart
          data={categoryTrend}
          timeLabel={activeWindow.label}
          selected={selectedLicenses}
          viewMode={viewMode}
          metric={metric}
          smoothingWeeks={smoothingWeeks}
          onViewChange={setViewMode}
          onMetricChange={setMetric}
        />
      </Box>
      <Box className="grid grid-cols-1 @5xl:grid-cols-[3fr_2fr] gap-6 mb-6">
        <CategoryStatsTable
          data={categoryStats}
          timeLabel={activeWindow.label}
          selected={selectedLicenses}
          onToggle={toggleLicense}
        />
        {snapshot.quizScoreDistribution.length > 0 && (
          <QuizDistributionChart data={snapshot.quizScoreDistribution} />
        )}
      </Box>
    </Box>
  )
}
